import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, MessageCircle, Clock, Send, Loader2 } from "lucide-react";
import { z } from "zod";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import AttachmentPicker, { UploadedAttachment } from "@/components/support/AttachmentPicker";

const CONTACT_EMAIL = "contact@clickandgard.fr";

const contactSchema = z.object({
  name: z.string().trim().min(2, "Nom requis (2 caractères min)").max(120),
  email: z.string().trim().email("Email invalide").max(255),
  subject: z.string().trim().min(3, "Sujet trop court").max(200),
  message: z.string().trim().min(10, "Message trop court (10 caractères min)").max(5000),
});

const Contact = () => {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: "",
    email: user?.email ?? "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [attachments, setAttachments] = useState<UploadedAttachment[]>([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = contactSchema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.errors[0]?.message ?? "Formulaire invalide");
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("send-contact-message", {
        body: { ...parsed.data, attachments },
      });
      if (error) throw error;
      setSent(true);
      toast.success("Message envoyé — vous recevrez une confirmation par email");
      setForm({ name: "", email: user?.email ?? "", subject: "", message: "" });
      setAttachments([]);
    } catch (err) {
      console.error(err);
      toast.error("Impossible d'envoyer le message. Réessayez plus tard.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title="Contact"
        description="Contactez l'équipe Click&Gard. Une question, un retour, un partenariat ? Notre support vous répond rapidement par e-mail."
      />
      <Navbar />
      <main className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10 animate-fade-in">
            <div className="inline-flex w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-secondary items-center justify-center shadow-neon mb-5">
              <MessageCircle className="w-8 h-8 text-primary-foreground" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold font-display tracking-tight mb-4">
              Nous <span className="text-gradient-static">contacter</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto">
              Une question, un retour ou un besoin d'aide ? Écrivez-nous, nous répondons sous 24 à 48h ouvrées.
            </p>
          </div>

          <Card className="card-glass-premium p-6 md:p-8 rounded-3xl mb-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center shadow-neon shrink-0">
                <Mail className="w-7 h-7 text-primary" />
              </div>
              <div className="text-center sm:text-left flex-1 min-w-0">
                <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wide font-semibold">
                  Email direct
                </p>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="text-xl md:text-2xl font-bold text-foreground hover:text-primary transition-colors break-all"
                >
                  {CONTACT_EMAIL}
                </a>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2 justify-center sm:justify-start">
                  <Clock className="w-4 h-4" />
                  <span>Réponse sous 24 à 48 heures ouvrées</span>
                </div>
              </div>
            </div>
          </Card>

          <Card className="card-glass-premium p-6 md:p-8 rounded-3xl">
            <h2 className="text-2xl font-bold mb-2">Formulaire de contact</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Remplissez ce formulaire — vous recevrez un email de confirmation
              {user ? " et une notification dans l'application dès qu'on vous répond." : "."}
            </p>

            {sent ? (
              <div className="text-center py-8">
                <div className="inline-flex w-14 h-14 rounded-full bg-green-500/10 items-center justify-center mb-4">
                  <Send className="w-7 h-7 text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-2">Message envoyé ✅</h3>
                <p className="text-muted-foreground mb-6">
                  Vous allez recevoir un email de confirmation. Notre équipe vous répond très vite.
                </p>
                <Button onClick={() => setSent(false)} variant="outline">
                  Envoyer un autre message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact-name">Nom complet *</Label>
                    <Input
                      id="contact-name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Camille Dupont"
                      maxLength={120}
                      required
                      disabled={submitting}
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact-email">Email *</Label>
                    <Input
                      id="contact-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="vous@exemple.com"
                      maxLength={255}
                      required
                      disabled={submitting}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="contact-subject">Sujet *</Label>
                  <Input
                    id="contact-subject"
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    placeholder="Sujet de votre demande"
                    maxLength={200}
                    required
                    disabled={submitting}
                  />
                </div>
                <div>
                  <Label htmlFor="contact-message">Message *</Label>
                  <Textarea
                    id="contact-message"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Décrivez votre demande en quelques lignes…"
                    rows={6}
                    maxLength={5000}
                    required
                    disabled={submitting}
                  />
                  <p className="text-xs text-muted-foreground mt-1 text-right">
                    {form.message.length}/5000
                  </p>
                </div>

                <div>
                  <Label>Pièces jointes (optionnel)</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Images, vidéos, PDF — 25 Mo max par fichier, 10 fichiers maximum.
                  </p>
                  <AttachmentPicker
                    bucket="contact-attachments"
                    value={attachments}
                    onChange={setAttachments}
                    disabled={submitting}
                  />
                </div>


                <Button
                  type="submit"
                  size="lg"
                  disabled={submitting}
                  className="w-full sm:w-auto rounded-2xl bg-gradient-to-r from-primary to-secondary shadow-neon hover:opacity-90 btn-neon font-semibold"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Envoi en cours…
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Envoyer le message
                    </>
                  )}
                </Button>
              </form>
            )}
          </Card>

          <div className="mt-8 text-center">
            <Link to="/" className="text-sm text-muted-foreground hover:text-primary transition-colors">
              ← Retour à l'accueil
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Contact;
